// src/components/Update/AddTodoModal/AddTodoModal.jsx
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import "./AddTodoModal.scss";

import { createPortal } from "react-dom";

// reusable UI
import InputField from "../../InputComponent/InputField";
import AddButton from "../../AddButton";
import ButtonWithIcon from "../../ButtonwithIcon/ButtonWithIcon";
import DeleteButton from "../../DeleteButton/DeleteButton";

import { isForbiddenAssignment, normalizeRole } from "../../../config/roles";

export default function AddTodoModal({
  open,
  onClose,
  taskId,
  stagePositionId,
  accessId,
  projectMembers = [],
  onCreateTodo,
  currentUserRole,
  context = "todo",
}) {
  const titleRef = useRef(null);

  // ---------------- state ----------------
  const [toDoTitle, setTitle] = useState("");
  const [remark, setRemark] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assignedEmp, setAssignedEmp] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [checkRows, setCheckRows] = useState([{ id: 1, title: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const canSubmit = open && toDoTitle.trim() && taskId && stagePositionId;

  // ---------------- helpers ----------------
  const members = useMemo(
    () => (Array.isArray(projectMembers) ? projectMembers : []),
    [projectMembers],
  );

  useEffect(() => {
    if (!open) {
      // 🔁 RESET ALL MODAL STATE HERE
      setTitle("");
      setRemark("");
      setPriority("MEDIUM");
      setAssignedEmp(null);
      setAssignModalOpen(false);

      setCheckRows([{ id: Date.now(), title: "" }]);
      setSubmitting(false);
      setIsClosing(false);
    }
  }, [open]);

  const initials = (name = "") =>
    name
      .split(" ")
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();

  const closeWithAnimation = useCallback(() => {
    setIsClosing(true);

    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 160);
  }, [onClose]);
  // ---------------- effects ----------------
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeWithAnimation();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, closeWithAnimation]);

  // ---------------- checklist helpers ----------------
  const addRow = () =>
    setCheckRows((p) => [...p, { id: Date.now(), title: "" }]);
  const removeRow = (id) => setCheckRows((p) => p.filter((r) => r.id !== id));
  const updateRow = (id, v) =>
    setCheckRows((p) => p.map((r) => (r.id === id ? { ...r, title: v } : r)));

  // ---------------- assign modal ----------------
  const AssignModal = ({ open, onClose }) => {
    if (!open) return null;

    // ❌ REMOVE VIEWER + CLIENT HERE
    const filteredMembers = members.filter((m) => {
      const role = normalizeRole(m.roleType ?? m.roleId ?? m.role);
      return role !== "VIEWER" && role !== "CLIENT";
    });

    return (
      <div className="cp-modal-assign-overlay">
        <div className="cp-modal-assign modal7865-content">
          <div className="cp-modal-assign-header">
            <h4>Assign member to ToDo</h4>
            <button className="close-btn" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="cp-modal-assign-body">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No eligible members</td>
                  </tr>
                ) : (
                  filteredMembers.map((m) => {
                    const isAssigned =
                      assignedEmp &&
                      Number(assignedEmp.empId) === Number(m.empId);

                    const targetRole = normalizeRole(
                      m.roleType ?? m.roleId ?? m.role,
                    );

                    const blocked = isForbiddenAssignment(
                      currentUserRole,
                      targetRole,
                      context,
                    );

                    return (
                      <tr key={m.empId}>
                        <td>{m.empName}</td>
                        <td>{m.roleType}</td>
                        <td>
                          {isAssigned ? (
                            <DeleteButton
                              size={36}
                              onClick={() => setAssignedEmp(null)}
                            />
                          ) : blocked ? (
                            <span className="assign-blocked">Not allowed</span>
                          ) : (
                            <AddButton
                              size={26}
                              label="Add"
                              onClick={() => {
                                setAssignedEmp({
                                  empId: m.empId,
                                  empName: m.empName,
                                  roleType: m.roleType,
                                  accessId: m.accessId ?? null,
                                });
                                onClose();
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };
  const resolveAccessId = () => {
    // 1️⃣ selected assignee
    if (assignedEmp?.accessId) return assignedEmp.accessId;

    // 2️⃣ current logged-in user
    try {
      const user = JSON.parse(sessionStorage.getItem("user") || "null");
      if (user?.empId && Array.isArray(projectMembers)) {
        const me = projectMembers.find(
          (m) => String(m.empId) === String(user.empId),
        );
        if (me?.accessId) return me.accessId;
      }
    } catch {}

    // 3️⃣ project-level fallback
    return accessId ?? null;
  };
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        titleRef.current?.focus();
      }, 0);
    }
  }, [open]);

  // ---------------- create todo ----------------
  const handleCreate = async () => {
    if (!canSubmit || submitting) return;

    setSubmitting(true);
    try {
      const payload = {
        toDoTitle: toDoTitle.trim(),
        remark: remark?.trim() || null,
        priority,
        taskId: Number(taskId),
        stagePositionId: 1,
        assignedEmpId: assignedEmp?.empId ?? null,
        accessId: resolveAccessId(),

        // ✅ MATCHES BACKEND SCHEMA EXACTLY
        listRequestDTOS: checkRows
          .map((r) => r.title.trim())
          .filter(Boolean)
          .map((title) => ({
            toDoListTitle: title,
            completion: false, // default on create
          })),
      };

      await onCreateTodo?.(payload);
      closeWithAnimation();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="cp-modal-addtodo-overlay">
      <div
        className={`cp-modal-addtodo modal7865-content ${
          isClosing ? "closing" : ""
        }`}
      >
        {/* Header */}
        <div className="cp-modal-addtodo-header">
          <h5>Add Task</h5>
          <button className="close-btn" onClick={closeWithAnimation}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="add-todo-modal">
          <InputField
            id="todo-title"
            label="Title"
            value={toDoTitle}
            inputRef={titleRef} // 👈 ADD THIS
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter task title"
          />

          <InputField
            id="todo-remark"
            label="Remark"
            as="textarea"
            rows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Optional remark"
          />

          <div className="assign-row">
            {assignedEmp ? (
              <div className="assigned-chip">
                <span className="avatar">{initials(assignedEmp.empName)}</span>
                {assignedEmp.empName}
              </div>
            ) : (
              <span className="unassigned">Unassigned</span>
            )}

            <ButtonWithIcon
              text={assignedEmp ? "Reassign" : "Assign"}
              icon="mdi:account-plus"
              onClick={() => setAssignModalOpen(true)}
            />
          </div>

          {/* Checklist */}
          <div className="checklist">
            <AddButton label="Add Checklist" onClick={addRow} size={26} />
            {checkRows.map((r) => (
              <div key={r.id} className="checklist-row">
                <InputField
                  value={r.title}
                  onChange={(e) => updateRow(r.id, e.target.value)}
                  placeholder="Checklist item"
                />
                <button
                  type="button"
                  className="checklist-cancel-btn"
                  onClick={() => removeRow(r.id)}
                >
                  Cancel
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="cp-modal-addtodo-footer">
          <ButtonWithIcon
            text="Cancel"
            variant="outlined"
            onClick={closeWithAnimation}
          />
          <ButtonWithIcon
            text={submitting ? "Creating…" : "Create Task"}
            onClick={handleCreate}
            disabled={!canSubmit || submitting}
          />
        </div>
      </div>

      <AssignModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
      />
    </div>,
    document.body,
  );
}

AddTodoModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
